import { useState } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { EMPTY_BASELINE, summaryText, verdictCellClass } from '../../utils/baselineComparison';

// Baseline Comparison panel, shared by the Alerts and Test Run Results pages (CLAUDE.md rule #13).
// It renders a `resolveBaselineComparison()` result: the pipeline's stored alert-time comparison when the row
// carries one, else the portal's own computation of the same shape (utils/baselineComparison.js). The pages
// keep their own state (refresh in flight, the baseline-IP drawer) and pass it in.

function Section({ title, titleClass, defaultCollapsed, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div>
      <div
        className="flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}
      >
        {collapsed
          ? <ChevronRightIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
          : <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
        <span className={`text-xs ${titleClass} font-semibold uppercase tracking-wide`}>{title}</span>
      </div>
      {!collapsed && <div className="mt-2">{children}</div>}
    </div>
  );
}

const windowTitle = (w) => (w && (w.start || w.end) ? `${w.start || '?'} – ${w.end || '?'}` : undefined);

function BaselineCell({ value, onClick }) {
  if (value == null || value === EMPTY_BASELINE) return <span className="text-gray-600 italic">{EMPTY_BASELINE}</span>;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left text-gray-300 font-mono underline decoration-dotted underline-offset-2 hover:text-sky-300">
        {value}
      </button>
    );
  }
  return <span className="text-gray-300 font-mono">{value}</span>;
}

/**
 * Props:
 *   comparison     resolveBaselineComparison() result
 *   defaultOpen    expand on first render (sign-in alerts)
 *   eventIp        the IP the refresh button re-enriches (no button when empty)
 *   canRefresh     false in Demo Mode
 *   refreshing     a refresh for this IP is in flight
 *   refreshError   message to show above the table
 *   onRefresh      () => void
 *   onOpenIpDrawer (lists: { '7d': string[], '30d': string[] }) => void
 */
export default function BaselineComparisonPanel({
  comparison, defaultOpen = false, eventIp, canRefresh = true, refreshing = false, refreshError, onRefresh, onOpenIpDrawer,
}) {
  const c = comparison || { source: 'none', rows: [], deviations: 0, partials: 0 };
  const summary = summaryText(c.deviations, c.partials);
  const alertTimeSummary = c.alertTime ? (summaryText(c.alertTime.deviations, c.alertTime.partials) || 'no deviations') : null;
  const titleText = [
    'Baseline Comparison',
    summary,
    alertTimeSummary && alertTimeSummary !== summary ? `at alert time: ${alertTimeSummary}` : '',
  ].filter(Boolean).join('  ·  ');
  const titleClass = c.deviations > 0 || c.partials > 0 ? 'text-amber-400' : 'text-gray-400';
  const hasIpLists = !!(c.ipLists && (c.ipLists['7d'].length || c.ipLists['30d'].length));

  return (
    <Section title={titleText} titleClass={titleClass} defaultCollapsed={!defaultOpen}>
      {c.source === 'none' ? (
        <p className="text-gray-600 italic mt-1 text-[10px]">No baseline data available for this alert.</p>
      ) : (
        <div className="mt-1 text-[10px]">
          {refreshError && <div className="text-red-400 text-[10px] mb-1.5">{refreshError}</div>}
          <div className="grid grid-cols-4 gap-x-6 text-gray-400 uppercase tracking-wide text-[10px] mb-1.5 pb-1 border-b border-gray-800/40">
            <span>Field</span>
            <span className="inline-flex items-center gap-1">
              Event value
              {eventIp && canRefresh && onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  title={`Re-fetch IP enrichment from ProxyCheck.io for ${eventIp}`}
                  className="inline-flex items-center text-gray-500 hover:text-sky-300 disabled:opacity-50 normal-case"
                >
                  <ArrowPathIcon className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
            </span>
            <span title={windowTitle(c.windows?.['7d'])}>7-day baseline</span>
            <span title={windowTitle(c.windows?.['30d'])}>30-day baseline</span>
          </div>
          {c.rows.map((r) => {
            const drawer = r.key === 'ip' && hasIpLists && onOpenIpDrawer ? () => onOpenIpDrawer(c.ipLists) : null;
            return (
              <div key={r.key} className="grid grid-cols-4 gap-x-6 py-0.5 border-b border-gray-800/20 last:border-0">
                <span className="text-gray-400">{r.label}</span>
                <span className="min-w-0">
                  <span className={verdictCellClass(r.verdict)}>{r.event ?? '—'}</span>
                  {r.refreshed && (
                    <span
                      className="ml-1.5 text-[9px] text-sky-300 normal-case"
                      title="Re-checked after an IP enrichment refresh. The alert-time result is in the panel heading."
                    >
                      updated after IP refresh
                    </span>
                  )}
                </span>
                <span><BaselineCell value={r.baseline_7d} onClick={drawer} /></span>
                <span><BaselineCell value={r.baseline_30d} onClick={drawer} /></span>
              </div>
            );
          })}
          <p className="mt-1.5 text-[9px] text-gray-600">
            {c.source === 'stored'
              ? 'Compared at alert time by the detection pipeline.'
              : 'Compared in the portal: this alert predates the alert-time comparison.'}
          </p>
        </div>
      )}
    </Section>
  );
}
